import { useEffect, useMemo, useState } from "react";
import { Stack, CaretDown } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { api } from "@/lib/api";

const ON_FLOOR = ["In Stock", "Received"];
const num = (n) => Number(n) || 0;
const sizeLabel = (l, w) =>
  l || w ? `${Math.round(num(l))}" × ${Math.round(num(w))}"` : "size on request";

const statusTone = (s) =>
  ON_FLOOR.includes(s || "In Stock")
    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
    : "border-amber-500/30 bg-amber-500/10 text-amber-300";

/**
 * Pick a real slab (bundle) or one of its child pieces ("Slabs in Inventory")
 * to add to the nesting board. Fetches the full slab list once when opened so
 * every slab and piece is shown, unfiltered, regardless of status.
 * onPick(slabRecord, pieceRecordOrNull)
 */
export const InventorySlabPicker = ({
  onPick,
  quoteColor = "",
  thickness = "",
  buttonLabel = "From inventory",
  testId = "inv-slab-picker",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [stock, setStock] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || stock !== null || loading) return;
    setLoading(true);
    api
      .get("/slabs")
      .then(({ data }) => setStock(data || []))
      .catch(() => setStock([]))
      .finally(() => setLoading(false));
  }, [open, stock, loading]);

  // Slabs that match the quote's material/thickness float to the top (still shows all)
  const ordered = useMemo(() => {
    const list = [...(stock || [])];
    const matches = (s) =>
      (quoteColor && s.slab_color === quoteColor ? 2 : 0) +
      (thickness && s.thickness === thickness ? 1 : 0);
    return list.sort((a, b) => matches(b) - matches(a));
  }, [stock, quoteColor, thickness]);

  const choose = (slab, piece) => {
    onPick(slab, piece || null);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={`${testId}-trigger`}
          className={`h-9 gap-1.5 border-zinc-700 bg-transparent px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800 hover:text-white ${className}`}
        >
          <Stack size={14} weight="bold" /> {buttonLabel}
          <CaretDown size={12} weight="bold" className="text-zinc-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(32rem,94vw)] border-zinc-800 bg-zinc-900 p-0"
        data-testid={`${testId}-panel`}
      >
        <Command className="bg-transparent">
          <CommandInput
            placeholder="Search color, code, serial, bundle, rack…"
            data-testid={`${testId}-search`}
            className="h-11"
          />
          <CommandList className="max-h-[60vh]">
            {loading && (
              <div className="px-3 py-6 text-center text-xs text-zinc-500">
                Loading inventory…
              </div>
            )}
            {!loading && (
              <CommandEmpty className="px-3 py-6 text-xs text-zinc-500">
                No slabs in inventory match that search.
              </CommandEmpty>
            )}
            {!loading &&
              ordered.map((slab) => {
                const pieces = slab.pieces || [];
                const head =
                  `${slab.slab_color || "Slab"} · ${slab.material || ""} ${slab.thickness || ""}`.trim();
                const meta = [
                  slab.code,
                  slab.bundle_number ? `bundle ${slab.bundle_number}` : "",
                  slab.lot_number ? `lot ${slab.lot_number}` : "",
                ]
                  .filter(Boolean)
                  .join(" · ");
                const searchBase = `${slab.slab_color || ""} ${slab.material || ""} ${slab.thickness || ""} ${slab.code || ""} ${slab.bundle_number || ""} ${slab.lot_number || ""} ${slab.supplier || ""} ${slab.brand || ""}`;
                return (
                  <CommandGroup
                    key={slab.id}
                    data-testid={`${testId}-slab-${slab.id}`}
                    heading={
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-zinc-200">
                          {head}
                        </span>
                        {meta && (
                          <span className="font-mono-data text-[10px] text-zinc-500">
                            {meta}
                          </span>
                        )}
                      </span>
                    }
                  >
                    <CommandItem
                      value={`${searchBase} whole slab ${slab.id}`}
                      data-testid={`${testId}-slab-${slab.id}-whole`}
                      onSelect={() => choose(slab, null)}
                      className="cursor-pointer gap-2 text-sm aria-selected:bg-indigo-500/15"
                    >
                      <span className="min-w-0 flex-1 truncate text-indigo-300">
                        Use whole slab
                      </span>
                      <span className="font-mono-data shrink-0 text-[10px] text-zinc-500">
                        {sizeLabel(slab.length_in, slab.width_in)}
                      </span>
                    </CommandItem>
                    {pieces.map((p, i) => (
                      <CommandItem
                        key={p.id || i}
                        value={`${searchBase} ${p.serial_number || ""} ${p.location || ""} ${p.name || ""} ${p.id || i}`}
                        data-testid={`${testId}-piece-${p.id || `${slab.id}-${i}`}`}
                        onSelect={() => choose(slab, p)}
                        className="cursor-pointer gap-2 text-sm aria-selected:bg-indigo-500/15"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-mono-data text-yellow-500">
                            {p.serial_number || p.name || `Piece ${i + 1}`}
                          </span>
                          {p.location && (
                            <span className="ml-2 text-[11px] text-zinc-500">
                              rack {p.location}
                            </span>
                          )}
                        </span>
                        <span
                          className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusTone(
                            p.status,
                          )}`}
                        >
                          {p.status || "In Stock"}
                        </span>
                        <span className="font-mono-data shrink-0 text-[10px] text-zinc-500">
                          {sizeLabel(p.length_in, p.width_in)}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default InventorySlabPicker;
